import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import MicIcon from "@mui/icons-material/Mic";
import StopIcon from "@mui/icons-material/Stop";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { theme } from "./theme";

const RecordButton = styled(Fab)(({ disabled }: { disabled?: boolean }) => ({
  width: 80,
  height: 80,
  backgroundColor: disabled ? theme.colors.gray : theme.colors.primary,
  color: theme.colors.white,
  transition: "all 0.3s ease",
  boxShadow: theme.shadows.medium,
  position: "fixed",
  bottom: "2rem",
  left: "50%",
  transform: "translateX(-50%)",
  zIndex: 100,
  touchAction: "manipulation",
  WebkitTapHighlightColor: "transparent",
  outline: "none",
  "&:active": {
    transform: "translateX(-50%) scale(0.95)",
    backgroundColor: theme.colors.primaryDark,
  },
  "&:hover": {
    backgroundColor: theme.colors.primaryDark,
    transform: "translateX(-50%) scale(1.05)",
    boxShadow: theme.shadows.large,
  },
  "&.recording": {
    animation: "pulse 1.5s infinite",
    backgroundColor: theme.colors.primaryDark,
  },
  "@keyframes pulse": {
    "0%": {
      boxShadow: `0 0 0 0 rgba(126, 87, 194, 0.4)`,
    },
    "70%": {
      boxShadow: `0 0 0 30px rgba(126, 87, 194, 0)`,
    },
    "100%": {
      boxShadow: `0 0 0 0 rgba(126, 87, 194, 0)`,
    },
  },
}));

const ClearButton = styled(IconButton)(() => ({
  color: theme.colors.gray,
  transition: "all 0.2s ease",
  position: "absolute",
  top: "12px",
  right: "12px",
  backgroundColor: "rgba(243, 229, 245, 0.5)",
  backdropFilter: "blur(4px)",
  padding: "6px",
  zIndex: 95,
  "&:hover": {
    color: theme.colors.primary,
    backgroundColor: "rgba(243, 229, 245, 0.8)",
  },
}));

const MessageBubble = styled(Box)(({ isUser }: { isUser: boolean }) => ({
  backgroundColor: isUser ? theme.colors.primary : theme.colors.white,
  color: isUser ? theme.colors.white : theme.colors.text,
  padding: theme.spacing.md,
  borderRadius: theme.borderRadius.md,
  maxWidth: "85%",
  marginLeft: isUser ? "auto" : theme.spacing.sm,
  marginRight: isUser ? theme.spacing.sm : "auto",
  marginBottom: theme.spacing.sm,
  boxShadow: theme.shadows.small,
  wordBreak: "break-word",
  position: "relative",
  "& .timestamp": {
    color: isUser ? "rgba(255, 255, 255, 0.9)" : theme.colors.primaryDark,
    fontWeight: 500,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    bottom: "15px",
    [isUser ? "right" : "left"]: "-10px",
    width: "20px",
    height: "20px",
    backgroundColor: "inherit",
    transform: "rotate(45deg)",
    zIndex: -1,
  },
}));

const StatusText = styled(Typography)(() => ({
  color: theme.colors.primaryDark,
  fontSize: "0.9rem",
  fontWeight: 600,
  textAlign: "center",
  position: "fixed",
  bottom: "7.5rem",
  left: 0,
  right: 0,
  zIndex: 99,
  padding: "0.5rem",
  backgroundColor: "rgba(243, 229, 245, 0.9)",
  backdropFilter: "blur(4px)",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
}));

const MessagesContainer = styled(Box)(() => ({
  flex: 1,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  padding: theme.spacing.md,
  paddingBottom: "7rem",
  paddingTop: "2.5rem",
  position: "relative",
  "&::-webkit-scrollbar": {
    width: "8px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.colors.secondary,
    borderRadius: "4px",
  },
}));

const TypingIndicator = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "4px",
  padding: "12px 24px",
  backgroundColor: theme.colors.white,
  borderRadius: theme.borderRadius.md,
  maxWidth: "fit-content",
  marginLeft: theme.spacing.sm,
  marginBottom: theme.spacing.sm,
  boxShadow: theme.shadows.small,
  "& .dot": {
    width: "8px",
    height: "8px",
    backgroundColor: theme.colors.primary,
    borderRadius: "50%",
    opacity: 0.6,
    animation: "typingAnimation 1.4s infinite",
    "&:nth-of-type(2)": {
      animationDelay: "0.2s",
    },
    "&:nth-of-type(3)": {
      animationDelay: "0.4s",
    },
  },
  "@keyframes typingAnimation": {
    "0%, 100%": {
      transform: "translateY(0)",
    },
    "50%": {
      transform: "translateY(-4px)",
      opacity: 1,
    },
  },
}));

const MessageTimestamp = styled(Typography)(() => ({
  fontSize: "0.7rem",
  color: theme.colors.primaryDark,
  fontWeight: 500,
  marginTop: "4px",
  opacity: 1,
}));

const ControlArea = styled(Box)(() => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "rgba(243, 229, 245, 0.8)",
  backdropFilter: "blur(10px)",
  padding: "1rem 0 6rem 0",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 90,
}));

interface Message {
  text: string;
  isUser: boolean;
  timestamp: number;
}

interface RecorderProps {
  status: string;
  isLoading: boolean;
  messages: Message[];
  onRecordingToggle: () => void;
  recordingTime: number;
  onClearConversation: () => void;
}

const Recorder: React.FC<RecorderProps> = ({
  status,
  isLoading,
  messages,
  onRecordingToggle,
  recordingTime,
  onClearConversation,
}) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs
      .toString()
      .padStart(2, "0")}`;
  };

  const formatMessageTime = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleClearConfirm = () => {
    onClearConversation();
    setOpenConfirmDialog(false);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
        background: theme.colors.background,
      }}
    >
      {/* Messages Section with Clear Button */}
      <MessagesContainer>
        {messages.length > 0 && (
          <Tooltip title="Clear conversation" placement="left">
            <ClearButton
              size="small"
              onClick={() => setOpenConfirmDialog(true)}
              aria-label="Clear chat"
            >
              <DeleteOutlineIcon fontSize="small" />
            </ClearButton>
          </Tooltip>
        )}

        {messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              opacity: 0.7,
            }}
          >
            <Typography variant="h6" color="text.secondary">
              Start a conversation with your coach
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Tap the microphone to begin
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => (
            <Box key={index}>
              <MessageBubble isUser={message.isUser}>
                <Typography>{message.text}</Typography>
                <Typography className="timestamp" variant="caption">
                  {formatMessageTime(message.timestamp)}
                </Typography>
              </MessageBubble>
            </Box>
          ))
        )}
        {isLoading && !status.includes("recording") && (
          <TypingIndicator>
            <div className="dot" />
            <div className="dot" />
            <div className="dot" />
          </TypingIndicator>
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {/* Floating Record Button */}
      <RecordButton
        className={status === "recording" ? "recording" : ""}
        onClick={onRecordingToggle}
        onTouchStart={() => {
          console.log("Touch start on record button");
        }}
        disabled={isLoading && status !== "recording"}
        color="primary"
        aria-label={
          status === "recording" ? "Stop recording" : "Start recording"
        }
      >
        {status === "recording" ? <StopIcon /> : <MicIcon />}
      </RecordButton>

      {/* Status Text */}
      {(status === "recording" || isLoading) && (
        <StatusText>
          {status === "recording"
            ? `Recording... ${formatTime(recordingTime)}`
            : isLoading
            ? "Your coach is thinking..."
            : ""}
        </StatusText>
      )}

      {/* Loading Indicator */}
      {isLoading && status === "recording" && (
        <Box
          sx={{
            position: "fixed",
            bottom: "7.5rem",
            left: "calc(50% - 12px)",
            zIndex: 100,
          }}
        >
          <CircularProgress size={24} sx={{ color: theme.colors.primary }} />
        </Box>
      )}

      {/* Add a centered bottom control area */}
      <ControlArea>{/* Placeholder for the control area */}</ControlArea>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          style: {
            borderRadius: theme.borderRadius.md,
            boxShadow: theme.shadows.medium,
          },
        }}
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ color: theme.colors.primary, fontWeight: 600 }}
        >
          Clear conversation?
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="alert-dialog-description"
            sx={{ color: theme.colors.text }}
          >
            Are you sure you want to clear the entire conversation history? This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            sx={{
              color: theme.colors.text,
              textTransform: "none",
              "&:hover": {
                backgroundColor: "rgba(126, 87, 194, 0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleClearConfirm}
            variant="contained"
            sx={{
              backgroundColor: theme.colors.primary,
              textTransform: "none",
              "&:hover": {
                backgroundColor: theme.colors.primaryDark,
              },
            }}
            autoFocus
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Recorder;
